import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { Target, TrendingUp, AlertTriangle, Calendar, RefreshCw, Zap } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar, Cell } from 'recharts';
import { metasAPI } from '../services/api';
import MetasTimeline from './MetasTimeline';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#84cc16', '#14b8a6'];

const MetasDashboard = ({ clienteId, onNavigateConfig }) => {
  const { darkMode } = useTheme();
  const { isAdmin } = useAuth();

  const [planos, setPlanos] = useState([]);
  const [planoSelecionado, setPlanoSelecionado] = useState(null);
  const [progresso, setProgresso] = useState(null);
  const [historico, setHistorico] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingProgresso, setLoadingProgresso] = useState(false);
  const [diasHistorico, setDiasHistorico] = useState(30);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [faseExpandida, setFaseExpandida] = useState(null);

  const cardBg = darkMode ? 'bg-zinc-800/80 border-gray-700/50' : 'bg-white border-gray-200';
  const textMain = darkMode ? 'text-white' : 'text-gray-900';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';

  // ─── Loaders ──────────────────────────────────────────────────────────────
  const loadPlanos = useCallback(async () => {
    if (!clienteId) return;
    setLoading(true);
    try {
      const res = await metasAPI.listarPlanos(clienteId);
      const lista = res.data || [];
      setPlanos(lista);
      if (lista.length >= 1 && !planoSelecionado) setPlanoSelecionado(lista[0].id);
    } catch { toast.error('Erro ao carregar planos'); }
    finally { setLoading(false); }
  }, [clienteId]);

  const loadProgresso = useCallback(async () => {
    if (!planoSelecionado) return;
    setLoadingProgresso(true);
    try {
      const res = await metasAPI.getProgresso(planoSelecionado);
      setProgresso(res.data);
    } catch { toast.error('Erro ao carregar progresso'); }
    finally { setLoadingProgresso(false); }
  }, [planoSelecionado]);

  const loadHistorico = useCallback(async () => {
    if (!planoSelecionado) return;
    try {
      const res = await metasAPI.getHistorico(planoSelecionado, diasHistorico);
      setHistorico(res.data);
    } catch {}
  }, [planoSelecionado, diasHistorico]);

  useEffect(() => { loadPlanos(); }, [loadPlanos]);
  useEffect(() => { loadProgresso(); }, [loadProgresso]);
  useEffect(() => { loadHistorico(); }, [loadHistorico]);

  // ─── Chart Data ───────────────────────────────────────────────────────────
  const chartData = useMemo(() => {
    if (!historico) return [];
    const { series = [] } = historico;
    // Apenas séries de segmento (regiões). Os totais por fase são linhas
    // tracejadas que confundem — misturam acumulado com parcial e poluem a
    // legenda. O progresso por fase já está na timeline/cards acima.
    const datasSet = new Set();
    series.forEach((s) => (s.dados || []).forEach((d) => datasSet.add(d.data)));
    const datas = [...datasSet].sort();

    // Preenche dias sem dados com null (connectNulls do Recharts desenha a
    // reta entre os pontos existentes sem despencar para zero)
    return datas.map((data) => {
      const ponto = { data: new Date(data + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) };
      series.forEach((s) => {
        const d = (s.dados || []).find((x) => x.data === data);
        ponto[s.segmento_label || s.segmento_id] = d ? d.valor : null;
      });
      return ponto;
    });
  }, [historico]);

  const chartLines = useMemo(() => {
    if (!historico) return [];
    // Só regiões, sem linhas de total por fase. Cores fixas por região para
    // consistência entre recarregamentos e períodos diferentes.
    return (historico.series || []).map((s, i) => ({
      key: s.segmento_label || s.segmento_id,
      color: COLORS[i % COLORS.length],
      dashed: false,
    }));
  }, [historico]);

  // ─── Backfill ─────────────────────────────────────────────────────────────
  const handleBackfill = async () => {
    setBackfillLoading(true);
    try {
      const res = await metasAPI.backfill(planoSelecionado, 60);
      toast.success(`✅ ${res.data?.snapshots_criados || 0} snapshots sincronizados (${res.data?.dias_processados || 0} dias)`);
      loadHistorico();
      loadProgresso();
    } catch { toast.error('Erro ao sincronizar'); }
    finally { setBackfillLoading(false); }
  };

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const pctColor = (pct) => pct >= 80 ? 'text-green-500' : pct >= 50 ? 'text-yellow-500' : 'text-red-500';
  const barColor = (pct) => pct >= 80 ? '#22c55e' : pct >= 50 ? '#eab308' : '#ef4444';
  const barBg = darkMode ? '#374151' : '#e5e7eb';
  const formatDate = (d) => { if (!d) return ''; const [, m, day] = d.split('-'); return `${day}/${m}`; };
  const hoje = new Date().toISOString().slice(0, 10);

  // ─── Loading / Empty ──────────────────────────────────────────────────────
  if (loading) return <div className={`rounded-xl p-12 text-center border ${cardBg}`}><div className="animate-pulse"><div className={`h-4 w-32 mx-auto rounded ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'}`} /></div></div>;

  if (planos.length === 0) return (
    <div className={`rounded-xl border p-12 text-center ${cardBg}`}>
      <Target size={40} className={`mx-auto mb-3 ${textMuted}`} />
      <p className={`text-base font-medium ${textMain}`}>Nenhum plano de meta</p>
      <p className={`text-sm mt-1 mb-4 ${textMuted}`}>Configure seu primeiro plano para acompanhar o progresso.</p>
      {onNavigateConfig && <button onClick={onNavigateConfig} className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700">⚙️ Configurar</button>}
    </div>
  );

  return (
    <div className="space-y-5">
      {/* ─── Top Bar: Plano + Actions ──────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {planos.length > 1 && (
            <select
              className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              value={planoSelecionado || ''}
              onChange={(e) => setPlanoSelecionado(e.target.value)}
            >
              {planos.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
          )}
          {planos.length === 1 && <h2 className={`text-base font-semibold ${textMain}`}>{planos[0].nome}</h2>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { loadProgresso(); loadHistorico(); }} className={`p-1.5 rounded-md ${darkMode ? 'hover:bg-zinc-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`} title="Atualizar"><RefreshCw size={15} /></button>
          {isAdmin() && planoSelecionado && (
            <button disabled={backfillLoading} onClick={handleBackfill} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-zinc-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} ${backfillLoading ? 'opacity-50' : ''}`}>
              {backfillLoading ? '⏳' : '🔄'} <span className="hidden sm:inline">{backfillLoading ? 'Sincronizando...' : 'Sync Histórico'}</span>
            </button>
          )}
        </div>
      </div>

      {/* ─── Hero: Progresso Principal ─────────────────────────────────── */}
      {progresso && !loadingProgresso && (
        <div className={`rounded-xl border p-6 ${cardBg}`}>
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className={`text-xs uppercase tracking-wide font-medium ${textMuted}`}>Progresso Geral</p>
              <div className="flex items-baseline gap-3 mt-1">
                <span className={`text-4xl font-bold ${pctColor(progresso.percentual || 0)}`}>
                  {(progresso.percentual || 0).toFixed(1)}%
                </span>
                <span className={`text-sm ${textMuted}`}>
                  {(progresso.atual_total || 0).toLocaleString()} de {(progresso.plano?.meta_total || 0).toLocaleString()}
                </span>
              </div>
            </div>
            <div className="text-right">
              <p className={`text-xs ${textMuted}`}>Faltam</p>
              <p className={`text-2xl font-bold ${textMain}`}>
                {Math.max(0, (progresso.plano?.meta_total || 0) - (progresso.atual_total || 0)).toLocaleString()}
              </p>
            </div>
          </div>
          {/* Barra grande */}
          <div className={`w-full h-4 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
            <div
              className="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-blue-500 to-green-500"
              style={{ width: `${Math.min(100, progresso.percentual || 0)}%` }}
            />
          </div>
        </div>
      )}

      {loadingProgresso && (
        <div className={`rounded-xl border p-8 text-center ${cardBg}`}>
          <div className="animate-pulse flex flex-col items-center gap-2">
            <div className={`h-8 w-24 rounded ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'}`} />
            <div className={`h-4 w-full rounded ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'}`} />
          </div>
        </div>
      )}

      {/* ─── Release Plan (Timeline) ─────────────────────────────────── */}
      {progresso && (progresso.fases || []).length > 0 && (
        <MetasTimeline fases={progresso.fases} darkMode={darkMode} />
      )}

      {/* ─── Fases Grid ───────────────────────────────────────────────── */}
      {progresso && (progresso.fases || []).length > 0 && (
        <div className="space-y-3">
          <h3 className={`text-xs uppercase tracking-wide font-semibold ${textMuted}`}>Por Fase</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(progresso.fases || []).map((faseData, idx) => {
              const { fase, atual_fase, progresso_fase, percentual, segmentos, divergencia_meta, status } = faseData;
              const naoIniciada = status === 'nao_iniciada';
              const pct = naoIniciada ? 0 : Math.min(100, Math.max(0, percentual || 0));
              const isAtiva = !naoIniciada && fase.data_inicio <= hoje && fase.data_fim >= hoje;
              const isExpanded = faseExpandida === fase.id;
              const isFutura = naoIniciada;

              return (
                <div
                  key={fase.id}
                  className={`rounded-xl border overflow-hidden cursor-pointer transition-all ${
                    isFutura ? (darkMode ? 'border-gray-700 opacity-60' : 'border-gray-200 opacity-70')
                    : isAtiva ? (darkMode ? 'border-blue-500/40 bg-blue-900/10' : 'border-blue-300 bg-blue-50/50')
                    : cardBg
                  } ${isExpanded ? 'md:col-span-2' : ''}`}
                  onClick={() => !naoIniciada && setFaseExpandida(isExpanded ? null : fase.id)}
                >
                  <div className="p-4">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {isAtiva && <Zap size={12} className="text-blue-400" />}
                        <span className={`text-sm font-semibold ${textMain}`}>{fase.nome}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${darkMode ? 'bg-zinc-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                          {formatDate(fase.data_inicio)} — {formatDate(fase.data_fim)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {divergencia_meta && (
                          <span
                            className="relative group cursor-help"
                            title={`A soma das metas dos segmentos (${faseData.soma_metas_segmentos?.toLocaleString('pt-BR') || '?'}) difere da meta total da fase (${fase.meta_fase?.toLocaleString('pt-BR') || '?'}). Ajuste na configuração de metas.`}
                          >
                            <AlertTriangle size={12} className="text-yellow-500" />
                          </span>
                        )}
                        <span className={`text-lg font-bold ${naoIniciada ? textMuted : pctColor(pct)}`}>{naoIniciada ? 'Aguardando' : `${pct.toFixed(0)}%`}</span>
                      </div>
                    </div>

                    {/* Barra */}
                    <div className={`w-full h-2.5 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div className="h-full rounded-full transition-all duration-300" style={{ width: `${pct}%`, backgroundColor: barColor(pct) }} />
                    </div>

                    {/* Info */}
                    <div className={`flex items-center justify-between mt-2 text-xs ${textMuted}`}>
                      <span>
                        {fase.baseline_valor > 0 ? `+${(progresso_fase || 0).toLocaleString()} (base: ${fase.baseline_valor.toLocaleString()})` : `${(atual_fase || 0).toLocaleString()}`}
                      </span>
                      <span>Meta: {(fase.meta_fase || 0).toLocaleString()}</span>
                    </div>

                    {/* Segmentos expandidos */}
                    {isExpanded && segmentos && segmentos.length > 0 && (
                      <div className={`mt-4 pt-3 border-t space-y-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        {segmentos.map((seg, si) => {
                          const progSeg = seg.progresso ?? seg.atual ?? 0;
                          const hasMeta = seg.meta_valor > 0;
                          const segPct = hasMeta ? Math.max(0, (progSeg / seg.meta_valor) * 100) : 0;
                          const barWidth = Math.min(100, segPct);
                          return (
                            <div key={seg.id}>
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[si % COLORS.length] }} />
                                  <span className={`text-xs font-medium ${textMain}`}>{seg.label}</span>
                                  {seg.baseline_valor != null && <span className={`text-[10px] ${textMuted}`}>(base: {seg.baseline_valor.toLocaleString()})</span>}
                                </div>
                                <span className={`text-xs ${textMuted}`}>
                                  +{progSeg.toLocaleString()}
                                  {hasMeta && <> / {seg.meta_valor.toLocaleString()}<span className={`ml-1 font-medium ${pctColor(segPct)}`}>{segPct.toFixed(0)}%</span></>}
                                  {!hasMeta && <span className={`ml-1 italic ${textMuted}`}>sem meta</span>}
                                </span>
                              </div>
                              {hasMeta && (
                                <div className={`w-full h-1.5 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                                  <div className="h-full rounded-full transition-all" style={{ width: `${barWidth}%`, backgroundColor: COLORS[si % COLORS.length] }} />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Hint para expandir */}
                    {!isExpanded && segmentos && segmentos.length > 0 && (
                      <p className={`text-[10px] mt-2 ${textMuted}`}>{segmentos.length} segmento{segmentos.length > 1 ? 's' : ''} — clique para expandir</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── Gráfico de Evolução ─────────────────────────────────────── */}
      {planoSelecionado && (
        <div className={`rounded-xl border p-5 ${cardBg}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-xs uppercase tracking-wide font-semibold ${textMuted}`}>Evolução</h3>
            <div className={`flex rounded-md border overflow-hidden ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              {[7, 15, 30].map((d) => (
                <button
                  key={d}
                  onClick={() => setDiasHistorico(d)}
                  className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${
                    diasHistorico === d
                      ? 'bg-blue-600 text-white'
                      : darkMode ? 'bg-zinc-800 text-gray-400 hover:bg-zinc-700' : 'bg-white text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>

          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chartData} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#27272a' : '#f3f4f6'} vertical={false} />
                <XAxis dataKey="data" stroke={darkMode ? '#6b7280' : '#9ca3af'} style={{ fontSize: '10px' }} tickLine={false} />
                <YAxis
                  stroke={darkMode ? '#6b7280' : '#9ca3af'}
                  style={{ fontSize: '10px' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: darkMode ? '#18181b' : '#fff',
                    border: `1px solid ${darkMode ? '#3f3f46' : '#e5e7eb'}`,
                    borderRadius: '10px', fontSize: '11px',
                    color: darkMode ? '#e4e4e7' : '#18181b',
                  }}
                  labelStyle={{ fontWeight: 600, marginBottom: 4 }}
                  formatter={(value, name) => [value?.toLocaleString('pt-BR') || '—', name]}
                  itemSorter={(item) => -(item.value || 0)}
                />
                <Legend wrapperStyle={{ fontSize: '10px', paddingTop: '8px' }} />
                {chartLines.map((line) => (
                  <Line
                    key={line.key}
                    type="monotone"
                    dataKey={line.key}
                    stroke={line.color}
                    strokeWidth={2.5}
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 0 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className={`text-center py-10 ${textMuted}`}>
              <Calendar size={24} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">Sem dados históricos ainda</p>
              <p className="text-xs mt-1">Snapshots são gerados diariamente às 03:30, ou use "Sync Histórico".</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MetasDashboard;
